<template>
  <div v-show="Display" class="status-form">
    <div class="form-container">
      <!-- Header -->
      <div class="form-header">
        <h2 class="form-title">Create Status</h2>
        <button @click="close" class="close-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <!-- Preview Area -->
      <div class="preview-area" :style="{ backgroundColor: selectedColor }" @click="$refs.imageInput.click()">
        <input
          ref="imageInput"
          type="file"
          accept="image/*"
          @change="handleImageUpload"
          class="file-input"
        />
        <div v-if="imagePreview" class="preview-image">
          <img :src="imagePreview" alt="Preview" />
          <button @click.stop="removeImage" class="remove-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div v-else-if="statusText" class="preview-text">
          {{ statusText }}
        </div>
        <div v-else class="preview-placeholder">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
          <p>Click to add image or add text below</p>
        </div>
      </div>

      <!-- Text Input -->
      <div class="form-section">
        <label class="section-label">Text</label>
        <textarea
          v-model="statusText"
          class="text-input"
          placeholder="What's on your mind?"
          maxlength="500"
          rows="3"
        ></textarea>
        <div class="char-count">{{ statusText.length }}/500</div>
      </div>

      <!-- Audio Upload/Record -->
      <div class="form-section">
        <label class="section-label">Audio</label>
        <div class="audio-controls">
          <!-- Upload Audio -->
          <div class="upload-area">
            <input
              ref="audioInput"
              type="file"
              accept="audio/*"
              @change="handleAudioUpload"
              class="file-input"
            />
            <button @click="$refs.audioInput.click()" class="upload-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 18V5l12-2v13"/>
                <circle cx="6" cy="18" r="3"/>
                <circle cx="18" cy="16" r="3"/>
              </svg>
              <span>{{ audioFile ? audioFile.name : 'Upload Audio' }}</span>
            </button>
          </div>

          <!-- Record Audio -->
          <button 
            @click="toggleRecording" 
            :class="['record-btn', { recording: isRecording }]"
          >
            <svg v-if="!isRecording" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="10"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="2"/>
            </svg>
            <span>{{ isRecording ? 'Stop Recording' : 'Record Audio' }}</span>
            <span v-if="isRecording" class="recording-time">{{ recordingTime }}s</span>
          </button>
        </div>

        <!-- Audio Preview -->
        <div v-if="audioPreview" class="audio-preview">
          <audio :src="audioPreview" controls></audio>
          <button @click="removeAudio" class="remove-audio-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Background Color -->
      <div class="form-section">
        <label class="section-label">Background Color</label>
        <div class="color-picker">
          <div
            v-for="color in $store.state.colors"
            :key="color"
            @click="selectedColor = color"
            :class="['color-option', { selected: selectedColor === color }]"
            :style="{ backgroundColor: color }"
          >
            <svg v-if="selectedColor === color" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="form-actions">
        <button @click="close" class="btn-cancel">Cancel</button>
        <button @click="handleSubmit" class="btn-submit" :disabled="!canSubmit">
          Create Status
        </button>
      </div>
    </div>
    <Loader :loading="loading" />
  </div>
</template>

<script>
import Loader from "@/views/components/loader/Loader.vue"

export default {
  name: 'StatusForm',
  components: {
    Loader
  },
  emits:["statusCreated"],
  data() {
    return {
      Display:false,
      loading:false,
      statusText: '',
      imageFile: null,
      imagePreview: null,
      audioFile: null,
      audioPreview: null,
      isRecording: false,
      recordingTime: 0,
      mediaRecorder: null,
      recordingTimer: null,
      audioChunks: [],
      selectedColor: '#c49b5e',
    }
  },
  computed: {
    canSubmit() {
      return this.statusText.trim() || this.imageFile || this.audioFile || this.audioPreview;
    }
  },
  methods: {
    show() {
      this.Display = true
    },
    close() {
      this.Display = false
      this.resetForm();
    },
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.imageFile = file;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    removeImage() {
      this.imageFile = null;
      this.imagePreview = null;
      this.$refs.imageInput.value = '';
    },
    handleAudioUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.audioFile = file;
        this.audioPreview = URL.createObjectURL(file);
      }
    },
    async toggleRecording() {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        await this.startRecording();
      }
    },
    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.mediaRecorder = new MediaRecorder(stream);
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (event) => {
          this.audioChunks.push(event.data);
        };

        this.mediaRecorder.onstop = () => {
          const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
          this.audioFile = new File([audioBlob], 'recorded-audio.webm', { type: 'audio/webm' });
          this.audioPreview = URL.createObjectURL(audioBlob);
          stream.getTracks().forEach(track => track.stop());
        };

        this.mediaRecorder.start();
        this.isRecording = true;
        this.recordingTime = 0;
        
        this.recordingTimer = setInterval(() => {
          this.recordingTime++;
          if (this.recordingTime >= 60) {
            this.stopRecording();
          }
        }, 1000);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        alert('Could not access microphone. Please check permissions.');
      }
    },
    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
        this.isRecording = false;
        clearInterval(this.recordingTimer);
      }
    },
    removeAudio() {
      this.audioFile = null;
      this.audioPreview = null;
      this.$refs.audioInput.value = '';
      if (this.isRecording) {
        this.stopRecording();
      }
    },
    async handleSubmit() {
      if (!this.canSubmit) return;
      this.loading = true
      const wallpaper = this.$store.state.colors.findIndex((color)=>color===this.selectedColor)
      const form = new FormData();
      form.append("type", "create");
      form.append("title", this.statusText);
      form.append("text", this.statusText);
      form.append("image", this.imageFile);
      form.append("audio", this.audioFile);
      form.append("wallpaper",wallpaper === -1 ? 0 : wallpaper);

      try {
          const res = await this.$axios.post('status.php',
              form,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
          )
          //console.log(res.data)
          this.$store.commit('addMyStatus',res.data)
          this.resetForm();
          this.$emit('statusCreated')
          this.close()
      } catch (e) {
        console.log(e.response?.data?.error || e.message);
      } finally { this.loading = false}
    },
    resetForm() {
      this.statusText = '';
      this.imageFile = null;
      this.imagePreview = null;
      this.audioFile = null;
      this.audioPreview = null;
      this.selectedColor = '#c49b5e';
      this.isRecording = false;
      this.recordingTime = 0;
      if (this.$refs.imageInput) this.$refs.imageInput.value = '';
      if (this.$refs.audioInput) this.$refs.audioInput.value = '';
    }
  },
  beforeUnmount() {
    if (this.isRecording) {
      this.stopRecording();
    }
  }
}
</script>

<style scoped>
.status-form {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 14, 38, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
  height: 100hv;
  box-sizing: border-box;
  margin-bottom: 120px;
}

.form-container {
  background: rgba(30, 41, 59, 0.95);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 100%;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

/* Header */
.form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.15);
  sticky: top 0;
  background: rgba(30, 41, 59, 0.95);
}

.form-title {
  color: #e0f7ff;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: #94a3b8;
  transition: all 0.3s;
  border-radius: 8px;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #e0f7ff;
}

.close-btn svg {
  width: 20px;
  height: 20px;
}

/* Preview Area */
.preview-area {
  margin: 16px;
  height: 160px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  transition: background-color 0.3s;
  cursor: pointer;
}

.preview-area:hover .preview-placeholder {
  opacity: 0.8;
}

.preview-image {
  width: 100%;
  height: 100%;
  position: relative;
}

.preview-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(10, 14, 38, 0.8);
  border: none;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  color: #ffffff;
  transition: all 0.3s;
}

.remove-btn:hover {
  background: rgba(239, 68, 68, 0.9);
}

.remove-btn svg {
  width: 20px;
  height: 20px;
}

.preview-text {
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  padding: 16px;
  word-break: break-word;
}

.preview-placeholder {
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  transition: opacity 0.3s;
}

.preview-placeholder svg {
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
}

.preview-placeholder p {
  margin: 0;
  font-size: 14px;
}

/* Form Sections */
.form-section {
  padding: 0 16px 12px;
}

.section-label {
  display: block;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
}

/* Text Input */
.text-input {
  width: 100%;
  background: rgba(10, 14, 38, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  padding: 10px;
  color: #e0f7ff;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  outline: none;
}

.text-input::placeholder {
  color: #94a3b8;
}

.text-input:focus {
  border-color: rgba(0, 212, 255, 0.5);
  box-shadow: 0 0 15px rgba(0, 212, 255, 0.2);
}

.char-count {
  text-align: right;
  color: #94a3b8;
  font-size: 11px;
  margin-top: 4px;
}

/* Upload Area */
.file-input {
  display: none;
}

.upload-area {
  margin-bottom: 8px;
}

.upload-btn {
  width: 100%;
  background: rgba(10, 14, 38, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  color: #e0f7ff;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
}

.upload-btn:hover {
  background: rgba(10, 14, 38, 0.8);
  border-color: rgba(0, 212, 255, 0.4);
}

.upload-btn svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.upload-btn span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Audio Controls */
.audio-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.record-btn {
  width: 100%;
  background: rgba(10, 14, 38, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  color: #e0f7ff;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
}

.record-btn.recording {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.5);
  color: #ef4444;
}

.record-btn:hover {
  background: rgba(10, 14, 38, 0.8);
  border-color: rgba(0, 212, 255, 0.4);
}

.record-btn.recording:hover {
  background: rgba(239, 68, 68, 0.3);
}

.record-btn svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.recording-time {
  margin-left: auto;
  font-weight: 600;
  font-size: 12px;
}

/* Audio Preview */
.audio-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  padding: 10px;
  background: rgba(10, 14, 38, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
}

.audio-preview audio {
  flex: 1;
  height: 30px;
}

.remove-audio-btn {
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: #ef4444;
  border-radius: 6px;
  flex-shrink: 0;
}

.remove-audio-btn:hover {
  background: rgba(239, 68, 68, 0.2);
}

.remove-audio-btn svg {
  width: 18px;
  height: 18px;
}

/* Color Picker */
.color-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.color-option {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.3s;
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.color-option:hover {
  transform: scale(1.1);
}

.color-option.selected {
  border-color: #e0f7ff;
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.5);
}

.color-option svg {
  width: 20px;
  height: 20px;
}

/* Action Buttons */
.form-actions {
  padding: 12px 16px;
  border-top: 1px solid rgba(0, 212, 255, 0.15);
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn-cancel,
.btn-submit {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
}

.btn-cancel {
  background: rgba(30, 41, 59, 0.8);
  color: #e0f7ff;
  border: 1px solid rgba(0, 212, 255, 0.2);
}

.btn-cancel:hover {
  background: rgba(30, 41, 59, 1);
  border-color: rgba(0, 212, 255, 0.4);
}

.btn-submit {
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 212, 255, 0.3);
}

.btn-submit:hover:not(:disabled) {
  box-shadow: 0 6px 16px rgba(0, 212, 255, 0.5);
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 640px) {
  .status-form {
    padding: 0px;
    margin-bottom: 0px;
  }
  .form-container {
    width: 100%;
    width: 100%;
    height: 100%;
    border-radius: 0;
    border: none;
  }

  .form-header,
  .form-section,
  .form-actions {
    padding-left: 12px;
    padding-right: 12px;
  }

  .preview-area {
    margin: 12px;
    height: 140px;
  }

  
  .color-option {
    width: 36px;
    height: 36px;
  }
  
  .btn-cancel,
  .btn-submit {
    padding: 9px 16px;
  }
}
</style>